/**
 * Puissances — logique pure (Boîte à outils, 5e → 2nde).
 *
 * Valeurs EXACTES en chaînes (BigInt) : 10²¹ s'écrit en entier, 2⁻³ = 1/8 =
 * 0,125, 3⁻² = 1/9. Notation scientifique par manipulation de chiffres
 * (jamais de flottant : 0,000 47 → 4,7 × 10⁻⁵). Règles de calcul (produit,
 * quotient, même exposant) expliquées en facteurs. Échelle des longueurs de
 * l'atome à la Voie lactée.
 */

export interface Step {
  tex: string;
  say: string;
}

const NNBSP = String.fromCharCode(0x202f);
const MINUS = '−';

const SUP: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '-': '⁻',
};
/** exposant en chiffres supérieurs : « ⁻⁵ » */
export const sup = (n: number) => [...String(n)].map((c) => SUP[c] ?? c).join('');
/** « 10⁻⁵ » pour les phrases */
export const tenTo = (n: number) => `10${sup(n)}`;

/**
 * exposants permis (programme de cycle 4, BO du 5 mars 2026, référence du
 * projet) : 5e carré et cube ; 4e exposants entiers naturels (a⁰ = 1) et
 * 10⁻ⁿ pour les préfixes nano → milli ; 3e exposants négatifs pour tous.
 */
export function exponentRange(rank: number, base: number): [number, number] {
  if (rank <= 2) return [2, 3];
  if (rank === 3) return base === 10 ? [-9, 9] : [0, 6];
  return [-6, 9];
}

/** « −3 » entre parenthèses dans une phrase : « 2 + (−5) » ; sinon « 4 » */
export const sgn = (v: number) => (v < 0 ? `(${MINUS}${-v})` : String(v));
/** « −3 » sans parenthèses */
export const signedText = (v: number) => (v < 0 ? `${MINUS}${-v}` : String(v));

/** groupe un entier écrit en chiffres par trois (espace fine insécable) */
export function groupInt(digits: string, sep = NNBSP): string {
  const out: string[] = [];
  for (let i = digits.length; i > 0; i -= 3) out.unshift(digits.slice(Math.max(0, i - 3), i));
  return out.join(sep);
}

/** partie décimale groupée par trois depuis la virgule */
function groupDec(digits: string, sep = NNBSP): string {
  const out: string[] = [];
  for (let i = 0; i < digits.length; i += 3) out.push(digits.slice(i, i + 3));
  return out.join(sep);
}

/** « 1 000 », « 0,001 » : 10ⁿ en écriture décimale */
export function tenPowerDecimal(n: number, sep = NNBSP): string {
  if (n >= 0) return groupInt(`1${'0'.repeat(n)}`, sep);
  return `0,${groupDec(`${'0'.repeat(-n - 1)}1`, sep)}`;
}

/** décimal → LaTeX : virgule {,} et espaces fines \, */
export const decTex = (s: string) =>
  s.replace(/−/g, '-').replace(',', '{,}').split(NNBSP).join('\\,');

export const powTex = (b: number | string, n: number) => {
  const base = typeof b === 'number' && b < 0 ? `(${b})` : String(b);
  return `${base}^{${n}}`;
};

/** b^n exact : entier, décimal fini (b = 2, 5, 10) ou fraction 1/bᵏ */
export function powerValue(b: number, n: number): { tex: string; plain: string; atoms: string[] } {
  if (n >= 0) {
    const v = (BigInt(b) ** BigInt(n)).toString();
    return { tex: decTex(groupInt(v)), plain: groupInt(v), atoms: [v] };
  }
  const den = BigInt(b) ** BigInt(-n);
  // décimal fini si den = 2^i 5^j
  let rest = den;
  let k = 0;
  let twos = 0;
  let fives = 0;
  while (rest % 2n === 0n) {
    rest /= 2n;
    twos++;
  }
  while (rest % 5n === 0n) {
    rest /= 5n;
    fives++;
  }
  if (rest === 1n && Math.max(twos, fives) <= 6) {
    k = Math.max(twos, fives);
    const num = 10n ** BigInt(k) / den;
    const digits = num.toString().padStart(k, '0');
    const plain = `0,${groupDec(digits)}`;
    return { tex: decTex(plain), plain, atoms: [`0,${digits}`] };
  }
  const d = den.toString();
  return {
    tex: `\\frac{1}{${decTex(groupInt(d))}}`,
    plain: `1/${groupInt(d)}`,
    atoms: ['1', '÷', d],
  };
}

/** b × b × … (n facteurs) ; 1/(b × b …) pour n < 0 */
export function expandTex(b: number | string, n: number, max = 8): string {
  const k = Math.abs(n);
  const base = typeof b === 'number' && b < 0 ? `(${b})` : String(b);
  const prod =
    k <= max
      ? Array.from({ length: k }, () => base).join(' \\times ')
      : `\\underbrace{${base} \\times ${base} \\times \\cdots \\times ${base}}_{${k}\\text{ facteurs}}`;
  if (n === 0) return '1';
  return n > 0 ? prod : `\\frac{1}{${prod}}`;
}

/** une puissance expliquée : définition, valeur */
export function powerSteps(b: number, n: number): Step[] {
  if (n === 0)
    return [
      {
        tex: `${powTex(b, 0)} = 1`,
        say: 'Par convention, un nombre non nul à la puissance 0 vaut 1.',
      },
    ];
  const v = powerValue(b, n);
  if (n === 1)
    return [{ tex: `${powTex(b, 1)} = ${b}`, say: `${b} à la puissance 1, c’est ${b} lui-même.` }];
  if (n > 0)
    return [
      {
        tex: `${powTex(b, n)} = ${expandTex(b, n)} = ${v.tex}`,
        say:
          n === 1
            ? `${b} à la puissance 1, c’est ${b} lui-même.`
            : `${b} à la puissance ${n} : ${n} facteurs égaux à ${b}.${n === 2 ? ` On dit «${NNBSP}${b} au carré${NNBSP}».` : n === 3 ? ` On dit «${NNBSP}${b} au cube${NNBSP}».` : ''}`,
      },
    ];
  return [
    {
      tex: `${powTex(b, n)} = \\frac{1}{${powTex(b, -n)}} = ${expandTex(b, n)} = ${v.tex}`,
      say: `Un exposant négatif : c’est l’inverse de ${b} à la puissance ${-n}.`,
    },
  ];
}

/* ------------------------------------------------------------------ */
/* notation scientifique                                               */
/* ------------------------------------------------------------------ */

/** « 0,000 47 », « 3 800 000 », « −12,5 » → chiffres significatifs D et exposant e (valeur = D × 10^e) */
export function parseDecimal(input: string): { neg: boolean; digits: string; e: number } | null {
  const s = input.replace(/\s/g, '').replace('−', '-').replace('.', ',');
  const m = s.match(/^(-?)(\d*)(?:,(\d*))?$/);
  if (!m || (!m[2] && !m[3])) return null;
  const neg = m[1] === '-';
  const intPart = m[2] ?? '';
  const decPart = m[3] ?? '';
  let digits = (intPart + decPart).replace(/^0+/, '');
  let e = -decPart.length;
  if (!digits) return { neg: false, digits: '0', e: 0 };
  while (digits.endsWith('0')) {
    digits = digits.slice(0, -1);
    e++;
  }
  return { neg, digits, e };
}

/** D × 10^e écrit en décimal, groupé */
export function digitsToDecimal(digits: string, e: number): string {
  if (digits === '0') return '0';
  if (e >= 0) return groupInt(digits + '0'.repeat(e));
  const k = -e;
  const padded = digits.padStart(k + 1, '0');
  const int = padded.slice(0, padded.length - k);
  const dec = padded.slice(padded.length - k).replace(/0+$/, '');
  return dec ? `${groupInt(int)},${groupDec(dec)}` : groupInt(int);
}

export interface Scientific {
  neg: boolean;
  /** « 4,7 » */
  mantissa: string;
  exp: number;
  /** écriture décimale du nombre */
  decimal: string;
  tex: string;
  plain: string;
  atoms: string[];
}

/** a × 10^k (a décimal quelconque) → notation scientifique */
export function scientific(input: string, k = 0): Scientific | null {
  const p = parseDecimal(input);
  if (!p) return null;
  if (p.digits === '0')
    return { neg: false, mantissa: '0', exp: 0, decimal: '0', tex: '0', plain: '0', atoms: ['0'] };
  const e = p.e + k;
  const exp = e + p.digits.length - 1;
  const mantissa = p.digits.length > 1 ? `${p.digits[0]},${p.digits.slice(1)}` : p.digits;
  const sign = p.neg ? '-' : '';
  const signPlain = p.neg ? MINUS : '';
  return {
    neg: p.neg,
    mantissa,
    exp,
    decimal: `${signPlain}${digitsToDecimal(p.digits, e)}`,
    tex: `${sign}${mantissa.replace(',', '{,}')} \\times 10^{${exp}}`,
    plain: `${signPlain}${mantissa} × ${tenTo(exp)}`,
    // exposant négatif entre parenthèses : la feuille le lit comme un seul exposant
    atoms: [
      ...(p.neg ? [MINUS] : []),
      mantissa,
      '×',
      '10',
      '^',
      ...(exp < 0 ? ['(', MINUS, String(-exp), ')'] : [String(exp)]),
    ],
  };
}

/** les étapes : d'où vient l'exposant (la virgule se déplace) */
export function scientificSteps(input: string, k = 0): Step[] {
  const p = parseDecimal(input);
  const sc = scientific(input, k);
  if (!p || !sc) return [];
  if (sc.mantissa === '0')
    return [
      {
        tex: '0',
        say: '0 n’a pas d’écriture scientifique : il n’a pas de premier chiffre non nul.',
      },
    ];
  const sign = p.neg ? '-' : '';
  const a = digitsToDecimal(p.digits, p.e);
  const start = k === 0 ? `${sign}${decTex(a)}` : `${sign}${decTex(a)} \\times 10^{${k}}`;
  const steps: Step[] = [];
  const shift = p.e + p.digits.length - 1; // déplacement de la virgule pour a seul
  if (shift === 0) {
    steps.push({
      tex: `${start} = ${sc.tex}`,
      say:
        k === 0
          ? `${a} est déjà entre 1 et 10 : on écrit × 10⁰.`
          : `${a} est déjà entre 1 et 10 : rien à déplacer.`,
    });
  } else {
    const dir = shift > 0 ? 'gauche' : 'droite';
    const ranks = Math.abs(shift);
    steps.push({
      tex: `${sign}${decTex(a)} = ${sign}${sc.mantissa.replace(',', '{,}')} \\times 10^{${shift}}`,
      say: `On place la virgule juste après le premier chiffre non nul : elle se déplace de ${ranks} rang${ranks > 1 ? 's' : ''} vers la ${dir}, donc on multiplie par ${tenTo(shift)} pour compenser.`,
    });
    if (k !== 0)
      steps.push({
        tex: `${start} = ${sign}${sc.mantissa.replace(',', '{,}')} \\times 10^{${shift}} \\times 10^{${k}} = ${sc.tex}`,
        say: `On additionne les exposants : ${signedText(shift)} + ${sgn(k)} = ${signedText(sc.exp)}.`,
      });
  }
  const m = sc.mantissa.replace(',', '{,}');
  steps.push(
    p.neg
      ? {
          tex: `1 \\leq ${m} < 10`,
          say: `Notation scientifique d’un nombre négatif : le signe −, puis un nombre entre 1 et 10 (exclu) multiplié par une puissance de 10. Sa distance à zéro est comprise entre ${tenTo(sc.exp)} et ${tenTo(sc.exp + 1)}.`,
        }
      : {
          tex: `1 \\leq ${m} < 10`,
          say: `Notation scientifique : un nombre entre 1 et 10 (exclu), multiplié par une puissance de 10. Le nombre est compris entre ${tenTo(sc.exp)} et ${tenTo(sc.exp + 1)}.`,
        },
  );
  return steps;
}

/* ------------------------------------------------------------------ */
/* règles de calcul                                                    */
/* ------------------------------------------------------------------ */

export type Rule = 'product' | 'quotient' | 'same-exponent';

const nTex = (n: number) => (n < 0 ? `(${n})` : String(n));

/** aⁿ × aᵐ, aᵐ ÷ aⁿ, aⁿ × bⁿ — avec le comptage des facteurs quand c'est court */
export function ruleSteps(rule: Rule, a: number, m: number, n: number, b = 5): Step[] {
  const small = m >= 0 && n >= 0 && m + n <= 8;
  if (rule === 'product') {
    const steps: Step[] = [];
    if (small && m > 0 && n > 0)
      steps.push({
        tex: `${powTex(a, m)} \\times ${powTex(a, n)} = (${expandTex(a, m)}) \\times (${expandTex(a, n)})`,
        say: `${m} facteur${m > 1 ? 's' : ''} ${a}, puis ${n} facteur${n > 1 ? 's' : ''} ${a}.`,
      });
    steps.push({
      tex: `${powTex(a, m)} \\times ${powTex(a, n)} = ${a}^{${nTex(m)} + ${nTex(n)}} = ${powTex(a, m + n)}`,
      say:
        m >= 0 && n >= 0
          ? `Même nombre ${a} : on additionne les exposants (${m + n} facteur${m + n > 1 ? 's' : ''} en tout).`
          : `Même nombre ${a} : on additionne les exposants, ${sgn(m)} + ${sgn(n)} = ${signedText(m + n)}.`,
    });
    return steps;
  }
  if (rule === 'quotient') {
    const steps: Step[] = [];
    if (small && m > 0 && n > 0) {
      const c = Math.min(m, n);
      steps.push({
        tex: `\\frac{${powTex(a, m)}}{${powTex(a, n)}} = \\frac{${expandTex(a, m)}}{${expandTex(a, n)}}`,
        say: `On simplifie ${c} facteur${c > 1 ? 's' : ''} ${a} en haut et en bas.`,
      });
    }
    const d = m - n;
    steps.push({
      tex: `\\frac{${powTex(a, m)}}{${powTex(a, n)}} = ${a}^{${nTex(m)} - ${nTex(n)}} = ${powTex(a, d)}${d < 0 ? ` = \\frac{1}{${powTex(a, -d)}}` : d === 0 ? ' = 1' : ''}`,
      say:
        m < 0 || n < 0
          ? `Même nombre ${a} : on soustrait les exposants, ${sgn(m)} − ${sgn(n)} = ${signedText(d)}.`
          : d === 0
            ? `Tout se simplifie : il reste 1, et ${a}⁰ = 1.`
            : d > 0
              ? `On soustrait les exposants : il reste ${d} facteur${d > 1 ? 's' : ''} ${a} en haut.`
              : `On soustrait les exposants : il reste ${-d} facteur${-d > 1 ? 's' : ''} ${a} en bas, d’où l’exposant négatif.`,
    });
    return steps;
  }
  // même exposant
  const steps: Step[] = [];
  if (m > 0 && m <= 4)
    steps.push({
      tex: `${powTex(a, m)} \\times ${powTex(b, m)} = ${Array.from({ length: m }, () => `(${a} \\times ${b})`).join(' \\times ')}`,
      say: `On range les facteurs par paires (${a} × ${b}).`,
    });
  steps.push({
    tex: `${powTex(a, m)} \\times ${powTex(b, m)} = (${a} \\times ${b})^{${m}} = ${powTex(a * b, m)}`,
    say: `Même exposant : on multiplie les nombres, l’exposant reste ${m}.`,
  });
  return steps;
}

/* ------------------------------------------------------------------ */
/* échelle des longueurs                                               */
/* ------------------------------------------------------------------ */

export interface ScaleObject {
  name: string;
  /** taille ≈ m × 10^e mètres */
  m: string;
  e: number;
}

export const SCALE: readonly ScaleObject[] = [
  { name: 'Atome d’hydrogène', m: '1', e: -10 },
  { name: 'Virus de la grippe', m: '1', e: -7 },
  { name: 'Globule rouge', m: '7', e: -6 },
  { name: 'Épaisseur d’un cheveu', m: '7', e: -5 },
  { name: 'Fourmi', m: '5', e: -3 },
  { name: 'Pièce de 1 euro', m: '2,3', e: -2 },
  { name: 'Être humain', m: '1,7', e: 0 },
  { name: 'Tour Eiffel', m: '3,3', e: 2 },
  { name: 'Mont Everest', m: '8,8', e: 3 },
  { name: 'Diamètre de la Terre', m: '1,3', e: 7 },
  { name: 'Distance Terre – Lune', m: '3,8', e: 8 },
  { name: 'Distance Terre – Soleil', m: '1,5', e: 11 },
  { name: 'Année-lumière', m: '9,5', e: 15 },
  { name: 'Diamètre de la Voie lactée', m: '1', e: 21 },
];

export const SCALE_MIN = -10;
export const SCALE_MAX = 21;

export const PREFIXES: Readonly<Record<number, { name: string; symbol: string }>> = {
  [-9]: { name: 'nano', symbol: 'nm' },
  [-6]: { name: 'micro', symbol: 'µm' },
  [-3]: { name: 'milli', symbol: 'mm' },
  [-2]: { name: 'centi', symbol: 'cm' },
  [-1]: { name: 'déci', symbol: 'dm' },
  1: { name: 'déca', symbol: 'dam' },
  2: { name: 'hecto', symbol: 'hm' },
  3: { name: 'kilo', symbol: 'km' },
  6: { name: 'méga', symbol: 'Mm' },
  9: { name: 'giga', symbol: 'Gm' },
  12: { name: 'téra', symbol: 'Tm' },
};

/** les objets dont la taille a cet ordre de grandeur (10^n ≤ taille < 10^(n+1)) */
export const objectsAt = (n: number) => SCALE.filter((o) => o.e === n);
